import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CommonService } from "../../services/CommonService";
import { DDLDataTable } from "../DDLDataTable";
import { DepartmentsInfoSetupFieldsPanel } from "./Panels/DepartmentsInfoSetupFieldsPanel";
import { DepartmentsService } from "../../services/DepartmentsService";
import { DepartmentsSetupToolbar } from "./DepartmentsSetupToolbar";
import { DepartmentsSetupValidationSchema } from "./validator";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IDepartments } from "../../models/Departments";
import { IGetListRequest } from "../../models";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { useNavigateToLogout } from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

// import { SingleColumnLayout } from "../DepartmentAssignemnt/SingleColumnLayout";




export interface IDepartmentsSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "departments-single-data-record";
const DEPARTMENTS_LIST_DATA_QUERY_KEY = "departments-list";

const useGetData = (
	clientId: string,
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, navigateToLogout],
		queryFn: () => {
			const url = id && `/v1/departments/${id}/?client_id=${clientId}`
			// return DepartmentsService.getById(clientId, id, navigateToLogout);
			return CommonService.getById(url, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useDepartments = (
	clientId: string,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY, params],
		queryFn: (data) => {
			const url = `/v1/departments/?client_id=${clientId}&order_by=${params.order_by}&order_direction=${params.order_direction}&page=${params.page}&size=${params.size}&search_query=${params.search_query}`
			// return DepartmentsService.getDepartments(
			// 	clientId,
			// 	params,
			// 	navigateToLogout
			// );
			return CommonService.getList(
				url,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});
const useGetDepartmentDDLItems = (clientId: string) =>
	useQuery({
		queryKey: ['department-ddl', clientId],
		queryFn: (data) => {
			return DepartmentsService.getGetDepartmentsDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});
export const DepartmentsSetup: React.FC<IDepartmentsSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [departmentsId, setDepartmentsId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "departmentName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "departmentName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending, data } = useDepartments(
		clientId,
		queryParams,
		navigateToLogout
	);

	const departmentsList: any | null = data;

	const { isPending: isPendingDepartmentDDLItems, data: departmentDDLItems } =
		useGetDepartmentDDLItems(clientId);


	const { isPending: isSubmitting, mutate: submitDepartmentsFormFields } =
		useMutation({
			mutationFn: (variables: any) =>
				CommonService.create(
					variables.createUrl,
					variables.updateUrl,
					variables.payload,
					variables.navigateToLogout,
				),
			onSuccess: (res: IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIDepartmentsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/company/departments/setup/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIDepartmentsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/company/departments/setup/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		clientId,
		editRecordId,
		navigateToLogout
	);

	const upadtedDepartmentsListItems = useMemo(() => {
		return (
			departmentsList &&
			departmentsList.items &&
			departmentsList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/company/departments/setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.departmentName}
					</Link>
				),
			}))
		);
	}, [departmentsList]);

	const onDepartmentSelectionChangeHandler = (selected: any) => {
		if (selected.value) {
			setEditRecordId(selected.value)
			navigate(`/company/departments/setup/${selected.value}`)
		} else {
			setEditRecordId(undefined);
			formMethods.reset();
			navigate(`/company/departments/setup`);
		}
	}


	const DEFAULT_VALUES = {
		code: undefined,
		departmentName: undefined,
		notes: undefined,

	};

	const formMethods = useForm<any>({
		resolver: yupResolver(DepartmentsSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (values: any) => {

		const payload = {
			clientId: clientId,
			id: id && id,
			navigateToLogout: navigateToLogout,
			departmentName: values.departmentName,
			code: values.code,
			notes: values.notes !== "" ? values.notes : undefined,
		};
		// submitDepartmentsFormFields(payload);
		// const createURL = `/v1/departments/?client_id=${clientId}`
		// const updateURL = `/v1/departments/${id}/?client_id=${clientId}`


	
		const ApiCredentials = {
			createUrl: `/v1/departments/?client_id=${clientId}`,
			updateUrl: id && `/v1/departments/${id}/?client_id=${clientId}`,
			payload: payload,
			navigateToLogout: navigateToLogout,
		}
		submitDepartmentsFormFields(ApiCredentials);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteDepartmentsById } = useMutation({
		mutationFn: (values: any) => CommonService.deleteById(
			values.url,
			values.navigateToLogout
		),

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the Departments fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/company/departments/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteDepartmentsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			// let delPayload = {
			// 	departmentsId: departmentsId,
			// 	navigateToLogout: navigateToLogout,
			// };			
			let deleteApiCredentials = {
				url: `/v1/departments/${departmentsId}/`,
				navigateToLogout: navigateToLogout,
			}
			deleteDepartmentsById(deleteApiCredentials);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setDepartmentsId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setDepartmentsId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveDepartmentsById } = useMutation({
		mutationFn: (values: any) => CommonService.archive(
			values.url,
			values.navigateToLogout
		),

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the Departments fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/company/departments/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [DEPARTMENTS_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveDepartmentsField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				departmentsId: departmentsId,
				navigateToLogout: navigateToLogout,
			};
			let archiveApiCredentials = {
				url: `/v1/departments/department-ddl?client_id=${clientId}`,
				navigateToLogout: navigateToLogout,
			}
			archiveDepartmentsById(archiveApiCredentials);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setDepartmentsId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/company/departments/setup`;
		navigate(path);
	};

	const expandedLabels = ["Company", "Departments"];
	const expandedDepartmentsSetupOrSetupItem = "Departments"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Departments" &&
				item.label === expandedDepartmentsSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedDepartmentsSetupOrSetupItem && "/company/departments/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		departmentsName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: IDepartments | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/company/departments/setup/${params.value}`;
		navigate(path);
	}, []);

	return (
		<Grid
		// sx={{
		// 	pb: "5rem",
		// 	"& .MuiAppBar-root": {
		// 		zIndex: 2,
		// 		top: subMenuVisibility ? "154px" : "112px",
		// 	},
		// }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive || isPendingDepartmentDDLItems}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteDepartmentsField}
				cancelActionHandler={onCloseHandler}
				testId="departments-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 5, lg: 4.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Departments"
										testId={`departments`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<DepartmentsInfoSetupFieldsPanel departmentDDLItems={departmentDDLItems} onDepartmentSelectionChangeHandler={onDepartmentSelectionChangeHandler} />
									</StandardPanel>
								}
								toolbar={
									<DepartmentsSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 2, lg: 3.4, xl: 5.4 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 5, lg: 4.1, xl: 3.1 }}
							sx={{ marginTop: "16px", display: { xs: "none", sm: "none", md: "block" } }}
						>
							<DDLDataTable
								rows={
									departmentsList && upadtedDepartmentsListItems
										? upadtedDepartmentsListItems
										: []
								}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									departmentsList && departmentsList.total
										? departmentsList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveDepartmentsField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"departments-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
